/**
 * Product sharing for the customer-facing product page.
 *
 * Uses the native Web Share API where the device/browser provides it, so the OS
 * share sheet can offer the apps the customer actually has installed (WhatsApp and
 * any other app that registers for sharing — including Instagram where the OS/browser
 * exposes it). This site never posts to any app directly: only the device's own share
 * sheet can do that, and it does so on the customer's behalf.
 *
 * Where Web Share is unavailable it falls back to copying the canonical product link.
 *
 * No login, no backend endpoint and no third-party sharing SDK is required.
 */

function trimOrigin(origin) {
  return String(origin || "").replace(/\/+$/, "");
}

/**
 * The canonical PUBLIC url of a product.
 *
 * `/product/:slug` is the exact route the storefront serves, so a link shared from
 * Shop, a category page, search results or a direct product URL all land the
 * recipient on that same product. A selected variant is carried as `?variant=<id>`
 * so the recipient lands on the same variant too. Only public identifiers are ever
 * included — never an admin/private path and never any customer information.
 */
export function productShareUrl({ slug, variantId, origin } = {}) {
  const cleanSlug = String(slug ?? "").trim();
  if (!cleanSlug) return "";
  const base = trimOrigin(origin || (typeof window !== "undefined" ? window.location.origin : ""));
  const url = `${base}/product/${encodeURIComponent(cleanSlug)}`;
  const cleanVariant = String(variantId ?? "").trim();
  return cleanVariant ? `${url}?variant=${encodeURIComponent(cleanVariant)}` : url;
}

/** Short accompanying text for the share sheet. */
export function productShareText(name) {
  const clean = String(name ?? "").trim();
  return clean
    ? `Check out "${clean}" on SHREE SHYAM GADGETS.`
    : "Check out this product on SHREE SHYAM GADGETS.";
}

function navOf(nav) {
  if (nav) return nav;
  return typeof navigator !== "undefined" ? navigator : null;
}

/** Whether this device/browser can handle the payload natively. */
export function canWebShare(payload, nav) {
  const n = navOf(nav);
  if (!n || typeof n.share !== "function") return false;
  if (typeof n.canShare === "function") {
    try { return !!n.canShare(payload); } catch { return false; }
  }
  return true;
}

/** Copy text to the clipboard (async Clipboard API first, then a legacy fallback). */
export async function copyText(text, nav) {
  const n = navOf(nav);
  try {
    if (n && n.clipboard && typeof n.clipboard.writeText === "function") {
      await n.clipboard.writeText(text);
      return { ok: true, method: "copy" };
    }
  } catch { /* fall through to the legacy path */ }
  try {
    if (typeof document === "undefined") return { ok: false, method: "copy", error: "unavailable" };
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.top = "-1000px";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    if (typeof el.setSelectionRange === "function") el.setSelectionRange(0, text.length);
    const ok = typeof document.execCommand === "function" ? document.execCommand("copy") : false;
    document.body.removeChild(el);
    return ok ? { ok: true, method: "copy" } : { ok: false, method: "copy", error: "copy-failed" };
  } catch {
    return { ok: false, method: "copy", error: "copy-failed" };
  }
}

/**
 * Share one product.
 * @returns {Promise<{ok: boolean, method: "share"|"copy", cancelled?: boolean}>}
 */
export async function shareProduct({ title, text, url } = {}, nav) {
  const payload = { title: String(title ?? ""), text: String(text ?? ""), url: String(url ?? "") };
  const n = navOf(nav);
  if (canWebShare(payload, n)) {
    try {
      await n.share(payload);
      return { ok: true, method: "share" };
    } catch (err) {
      // The customer dismissing the share sheet is not a failure.
      if (err && (err.name === "AbortError" || err.name === "NotAllowedError")) {
        return { ok: false, method: "share", cancelled: true };
      }
      // Anything else (e.g. the payload was refused): fall back to copying the link.
    }
  }
  return copyText(payload.url, n);
}